import React, { useState } from 'react';

const STATUS_COLORS = {
  paid: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
  pending: { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
  overdue: { bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
};

const DEMO_INVOICES = [
  { id: 'INV-1043', customer: 'Acme Corporation', amount: 4250.00, date: '2026-02-01', due: '2026-03-01', status: 'paid' },
  { id: 'INV-1044', customer: 'Summit Energy LLC', amount: 8700.00, date: '2026-02-03', due: '2026-03-03', status: 'pending' },
  { id: 'INV-1045', customer: 'Horizon Logistics', amount: 2150.00, date: '2026-01-15', due: '2026-02-15', status: 'overdue' },
  { id: 'INV-1046', customer: 'Prairie Wind Co-op', amount: 3400.00, date: '2026-02-05', due: '2026-03-05', status: 'pending' },
  { id: 'INV-1047', customer: 'Redstone Materials', amount: 6825.00, date: '2026-02-08', due: '2026-03-08', status: 'paid' },
];

export default function InvoicingPage() {
  const [invoices, setInvoices] = useState(DEMO_INVOICES);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ customer: '', amount: '', due: '' });

  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);
  const totalPending = invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.amount, 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0);

  const handleCreate = (e) => {
    e.preventDefault();
    if (!form.customer || !form.amount) return;
    const newInv = {
      id: `INV-${1048 + invoices.length - 5}`,
      customer: form.customer,
      amount: parseFloat(form.amount),
      date: new Date().toISOString().split('T')[0],
      due: form.due || new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
      status: 'pending'
    };
    setInvoices([newInv, ...invoices]);
    setForm({ customer: '', amount: '', due: '' });
    setShowModal(false);
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: 'var(--bg)',
    border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box'
  };

  return (
    <div className="page-body" style={{ padding: 24 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ color: 'var(--text)', fontSize: 26, fontWeight: 700, margin: '0 0 4px' }}>
            Invoicing
          </h1>
          <p className="page-subtitle" style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            {invoices.length} total invoices
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
          style={{
            padding: '10px 20px', background: 'var(--primary)', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
          }}
        >
          + Create Invoice
        </button>
      </div>

      {/* Summary Cards */}
      <div className="stats-grid" style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24
      }}>
        {[
          { label: 'Paid', value: `$${totalPaid.toLocaleString()}`, color: '#10b981' },
          { label: 'Pending', value: `$${totalPending.toLocaleString()}`, color: '#f59e0b' },
          { label: 'Overdue', value: `$${totalOverdue.toLocaleString()}`, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{
            padding: 18, background: 'var(--card)', borderRadius: 12,
            border: '1px solid var(--border)', textAlign: 'center'
          }}>
            <div className="stat-label" style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', fontWeight: 600 }}>
              {s.label}
            </div>
            <div className="stat-value" style={{ fontSize: 22, fontWeight: 700, color: s.color }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Invoice Table */}
      <div className="table-container" style={{
        background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Invoice', 'Customer', 'Amount', 'Date', 'Due Date', 'Status'].map(h => (
                <th key={h} style={{
                  padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)'
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '12px 16px', color: 'var(--primary)', fontSize: 13, fontWeight: 600 }}>{inv.id}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text)', fontSize: 13 }}>{inv.customer}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>
                  ${inv.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 13 }}>{inv.date}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 13 }}>{inv.due}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{
                    padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    textTransform: 'capitalize',
                    background: STATUS_COLORS[inv.status].bg,
                    color: STATUS_COLORS[inv.status].text
                  }}>
                    {inv.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000
        }}>
          <div className="modal" style={{
            width: '100%', maxWidth: 440, padding: 28, background: 'var(--card)',
            borderRadius: 14, border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, margin: 0 }}>Create Invoice</h2>
              <button onClick={() => setShowModal(false)} style={{
                background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer'
              }}>&times;</button>
            </div>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Customer</label>
                <input className="input" type="text" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })}
                  placeholder="Customer name" required style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Amount ($)</label>
                <input className="input" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00" required style={inputStyle} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Due Date</label>
                <input className="input" type="date" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{
                  flex: 1, padding: '10px 0', background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: 8, color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer'
                }}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{
                  flex: 1, padding: '10px 0', background: 'var(--primary)', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}>Create Invoice</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
